import { createDocEntryStore } from './docStore';
import { readState, writeState } from './docState';
import type { Rgb } from './sampleColour';
import type { MaterialPair } from './variantLabel';

// THE MATERIALS NO PHOTOGRAPH WILL EVER SHOW, and what they look like.
//
// Every other colour in this app is sampled off a picture, on purpose and at some length —
// see materialSurface, where a table mapping finish words to invented colours was taken out
// again because nobody had measured any of them. That rule is right and this is its one
// honest exception, for a reason that is about the PARTS and not about the rule:
//
// A screw, a spring washer, a barrel nut and a dowel are not visible in any photograph of the
// assembled product. They are inside the joint. The listing does not name them, the manual
// draws them as line art, and the crops row cannot find them because there is nothing to
// find. There is no reading to take, ever — so the choice here is not between a measurement
// and a guess, it is between a stated convention and nothing at all, which leaves a mesh
// wearing whatever the converter happened to write (OpenCASCADE's default is metallic, so an
// unpainted dowel arrives as an aluminium rod).
//
// AND THEY ARE THE SAME EVERYWHERE, which is why this is a module and not a per-notice
// setting. "Bois d'hévéa massif, foncé" is a decision one manufacturer made about one chair;
// a zinc-plated M6 barrel nut is a commodity, and the one in the next manual looks like the
// one in this one. A notice can still overrule it — a stored surface wins over these, see
// surfaceOf — which is what happens the day somebody has a photograph of black hardware.
//
// WHAT IS NOT HERE: the wood buttons that cap a screw hole. They ARE visible, they are
// finished to match the product, and they take their colour off the listing photographs like
// every other visible part. A part being small is not what puts it in this table; a part
// being unphotographable is.
//
// The words are fixed and untranslated, like every other material in this app — a retailer's
// words are shown as the retailer wrote them, and these are recorded onto a mesh's edit
// history ("undo steel, zinc-plated"), which must read the same next year whatever language
// the page happens to be in. Their roughness and metallic are not stated here: they are asked,
// with their textures (see defaultTexturesRun).
// AND ITS SURFACE: the same four numbers a photographed material is measured to (see Material's
// `texture`), asked of a model instead — these are inside the joint in every picture (see
// defaultTexturesRun). Absent until asked: flat, as they always were.
export type MaterialTexture = {
  contrast: number; chroma: number; anisotropy: number; periodMm: number;
  // Which of the two surfaces it is drawn as — see materialsRun's Pattern.
  pattern?: 'grain' | 'mottle';
};
// AND HOW IT TAKES THE LIGHT — roughness, metallic, whether it is fibrous — answered by the same
// call. Absent until asked, and a material without them is not painted.
export type StandardSurface = { roughness: number; metallic: number; fibre: boolean };
export type StandardMaterial = {
  pair: MaterialPair; rgb: Rgb; texture?: MaterialTexture;
} & Partial<StandardSurface>;

// WHAT A MANUAL STARTS WITH. Each manual's own list is editable in Phase 1 (Default
// materials), and these are what it holds until somebody edits it.
export const DEFAULT_MATERIALS: readonly StandardMaterial[] = [
  // ZINC-PLATED STEEL: screws, spring washers, barrel nuts, cam locks — the finish nearly all
  // flat-pack hardware arrives in, because it is what stops steel rusting for the least
  // money. Bright zinc over a clear passivate: a cool grey, a shade lighter and bluer than
  // bare steel's own reflectance (linear 0.56, 0.57, 0.58 — the published figure for iron).
  //
  // Black hardware exists and looks nothing like this. It is a different plating, and the
  // notice that has it can say so with a stored surface of its own.
  { pair: { material: 'Steel', color: 'zinc-plated' }, rgb: [200, 203, 206] },
  // RAW WOOD, for dowels. Not the product's wood: a dowel is beech or birch, unfinished, and
  // it is the one wooden part that is NOT the colour of the chair it goes into. Painting a
  // dowel with the stained hévéa the legs are made of would be wrong in the direction that
  // matters — dowels are pale, and every joint that shows one shows it pale.
  { pair: { material: 'Wood', color: 'dowel' }, rgb: [206, 176, 133] },
  // FELT, for the floor-protector pads under the feet. Beige is the commodity colour, the one
  // sold by the sheet; the other two are black and white, and both are a stored surface away.
  { pair: { material: 'Felt', color: 'beige' }, rgb: [203, 190, 168] },
  // PLASTIC, for the tools packed in the bag — the moulded screwdriver, a key's sleeve — and the
  // caps and glides that are not felt. In no photograph of the product either: a tool is not
  // part of it. Black is the commodity colour of a tool given away with the furniture; one that
  // came in another says so on its manual's list, as black hardware does.
  { pair: { material: 'Plastic', color: 'black' }, rgb: [40, 40, 43] },
];

// One material's identity, folded the way materialSurface folds it. Kept here rather than
// imported to avoid the cycle — materialSurface reads this file.
const keyOf = (pair: MaterialPair) =>
  `${pair.material.trim().toLowerCase()}|${pair.color.trim().toLowerCase()}`;

// THE LIST A MANUAL USES, editable in Phase 1's Default materials. Per manual and saved with
// it, like every other answer: a notice whose hardware is black says so on its own list
// without repainting every other manual's screws. Nothing saved means the defaults above.
const stateKey = (docId: string) => `notice.standardMaterials.${docId}`;

const store = createDocEntryStore<{ list: StandardMaterial[] }>((docId) => ({
  snapshot: { list: readState<StandardMaterial[]>(docId, stateKey(docId), [...DEFAULT_MATERIALS]) },
}));

export const useStandardMaterials = (docId: string) => store.useSnapshot(docId).list;
export const getStandardMaterials = (docId: string) => store.get(docId).list;

export function setStandardMaterials(docId: string, list: StandardMaterial[]): void {
  writeState(docId, stateKey(docId), list);
  store.patch(docId, { list });
}

// The colour this manual's list states for a material nobody can photograph, or undefined for
// every material not on it.
export function standardRgb(docId: string, pair: MaterialPair): Rgb | undefined {
  return getStandardMaterials(docId).find((m) => keyOf(m.pair) === keyOf(pair))?.rgb;
}

// Its roughness, metallic and fibre, where they have been asked for — only the keys it has.
export function standardSurface(docId: string, pair: MaterialPair): Partial<StandardSurface> {
  const m = getStandardMaterials(docId).find((one) => keyOf(one.pair) === keyOf(pair));
  if (!m || m.roughness == null || m.metallic == null) return {};
  return { roughness: m.roughness, metallic: m.metallic, fibre: m.fibre === true };
}

// And its surface, where it has been asked for.
export function standardTexture(docId: string, pair: MaterialPair): MaterialTexture | undefined {
  return getStandardMaterials(docId).find((m) => keyOf(m.pair) === keyOf(pair))?.texture;
}
